import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import {
  ArrowLeft,
  Clock3,
  Download,
  FileImage,
  Link2,
  Loader2,
  Package,
  Printer,
  ShieldCheck,
  User,
} from 'lucide-react'
import { ordersApi, type OrderReceiptInfo, type OrderResponse } from '@/services/ordersApi'
import {
  buildOrderDetailUrl,
  buildOrderSourceLabel,
  formatOrderMoney,
  getOrderConversationBadge,
  getOrderOriginBadge,
  getOrderStatusBadge,
} from './ordersUx'

const money = formatOrderMoney

function when(value: string | null | undefined) {
  if (!value) return 'Sin fecha'
  const date = new Date(value)
  return Number.isNaN(date.getTime())
    ? 'Sin fecha'
    : date.toLocaleString('es-ES', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
}

function badgeToneClass(tone: 'emerald' | 'blue' | 'violet' | 'slate' | 'amber' | 'indigo' | 'rose') {
  if (tone === 'emerald') return 'border-emerald-200 bg-emerald-50 text-emerald-700'
  if (tone === 'blue') return 'border-blue-200 bg-blue-50 text-blue-700'
  if (tone === 'violet') return 'border-violet-200 bg-violet-50 text-violet-700'
  if (tone === 'amber') return 'border-amber-200 bg-amber-50 text-amber-700'
  if (tone === 'indigo') return 'border-indigo-200 bg-indigo-50 text-indigo-700'
  if (tone === 'rose') return 'border-rose-200 bg-rose-50 text-rose-700'
  return 'border-slate-200 bg-slate-50 text-slate-600'
}

function infoToneClass(tone: 'emerald' | 'blue' | 'violet' | 'slate' | 'amber' | 'indigo' | 'rose') {
  if (tone === 'emerald') return 'border-emerald-200 bg-emerald-50 text-emerald-700'
  if (tone === 'blue') return 'border-blue-200 bg-blue-50 text-blue-700'
  if (tone === 'violet') return 'border-violet-200 bg-violet-50 text-violet-700'
  if (tone === 'amber') return 'border-amber-200 bg-amber-50 text-amber-700'
  if (tone === 'indigo') return 'border-indigo-200 bg-indigo-50 text-indigo-700'
  if (tone === 'rose') return 'border-rose-200 bg-rose-50 text-rose-700'
  return 'border-slate-200 bg-slate-50 text-slate-600'
}

interface ReceiptViewerProps {
  order: OrderResponse
  receiptInfo: OrderReceiptInfo | null
  receiptImageUrl: string | null
  onCopyRoute: () => Promise<void> | void
  onDownloadPng: () => void
  onDownloadPdf: () => void
  onPrint: () => void
}

export function OrderReceiptView({
  order,
  receiptInfo,
  receiptImageUrl,
  onCopyRoute,
  onDownloadPng,
  onDownloadPdf,
  onPrint,
}: ReceiptViewerProps) {
  const statusBadge = getOrderStatusBadge(order.status)
  const conversationBadge = getOrderConversationBadge(order.conversationId)
  const originBadge = getOrderOriginBadge({ source: order.source, conversationId: order.conversationId })
  const generatedAtLabel = receiptInfo ? when(receiptInfo.generatedAt) : 'Generado en backend'

  const orderMeta = [
    { label: 'Cliente', value: order.customerName || order.customerPhone || 'Sin cliente' },
    { label: 'Canal', value: buildOrderSourceLabel(order.source) },
    { label: 'Estado', value: statusBadge.label, tone: statusBadge.tone },
    { label: 'Fecha', value: when(order.createdAt) },
  ] as const

  return (
    <div className="min-h-screen bg-slate-100 px-4 py-6 print:bg-white print:px-0 print:py-0">
      <div className="mx-auto flex max-w-6xl flex-col gap-4">
        <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm print:rounded-none print:border-0 print:shadow-none">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="min-w-0">
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#075f55]">Recibo de orden</p>
              <h1 className="mt-1 text-2xl font-bold text-slate-900">{order.orderNumber}</h1>
              <p className="mt-1 text-sm text-slate-500">Elige una acción para continuar.</p>
            </div>

            <div className="flex flex-wrap gap-2 print:hidden">
              <Link
                to={buildOrderDetailUrl(order.id)}
                className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-50"
              >
                <ArrowLeft className="h-4 w-4" />
                Volver
              </Link>
              <button
                type="button"
                onClick={onDownloadPdf}
                className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-50"
              >
                <Download className="h-4 w-4" />
                Descargar PDF
              </button>
              <button
                type="button"
                onClick={onDownloadPng}
                disabled={!receiptImageUrl}
                className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <FileImage className="h-4 w-4" />
                Descargar PNG
              </button>
              <button
                type="button"
                onClick={onPrint}
                className="inline-flex items-center gap-2 rounded-full bg-[#075f55] px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#064d46]"
              >
                <Printer className="h-4 w-4" />
                Imprimir
              </button>
            </div>
          </div>

          <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {orderMeta.map((item) => (
              <InfoCard
                key={item.label}
                label={item.label}
                value={item.value}
                tone={'tone' in item ? item.tone : undefined}
              />
            ))}
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold ${badgeToneClass(conversationBadge.tone)}`}>
              {conversationBadge.label}
            </span>
            <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold ${badgeToneClass(originBadge.tone)}`}>
              {originBadge.label}
            </span>
            <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold ${badgeToneClass(statusBadge.tone)}`}>
              {statusBadge.label}
            </span>
            <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-600">
              <ShieldCheck className="mr-1.5 h-3.5 w-3.5" />
              Generado en {generatedAtLabel}
            </span>
          </div>
        </div>

        <div className="grid gap-4 xl:grid-cols-[minmax(0,1.3fr)_360px]">
          <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#075f55]">Vista previa PNG</p>
                <h2 className="mt-1 text-lg font-semibold text-slate-900">Recibo renderizado en backend</h2>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <FileImage className="h-4 w-4" />
                {receiptInfo?.fileName || 'receipt.png'}
              </div>
            </div>

            <div className="mt-4 overflow-hidden rounded-3xl border border-slate-200 bg-slate-50 p-2">
              {receiptImageUrl ? (
                <img
                  src={receiptImageUrl}
                  alt={`Recibo ${order.orderNumber}`}
                  className="h-auto w-full rounded-2xl"
                />
              ) : (
                <div className="flex min-h-[28rem] items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-white px-6 py-10 text-center">
                  <div>
                    <Loader2 className="mx-auto h-10 w-10 animate-spin text-[#075f55]" />
                    <p className="mt-3 text-sm font-semibold text-slate-900">Generando imagen del recibo</p>
                    <p className="mt-1 text-sm text-slate-500">Esto ocurre en el backend para que puedas enviarlo por WhatsApp.</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="space-y-4">
            <InfoPanel
              title="Ruta backend"
              icon={<Link2 className="h-4 w-4" />}
              tone="blue"
              value={receiptInfo?.downloadUrl || 'Generando...'}
              copyValue={receiptInfo?.downloadUrl}
              description="Ruta interna para descargar la imagen desde el backend."
              onCopy={onCopyRoute}
            />

            <InfoPanel
              title="Ruta local"
              icon={<Package className="h-4 w-4" />}
              tone="slate"
              value={receiptInfo?.filePath || 'Generando...'}
              description="Archivo generado en /storage/receipts para reutilizarlo o enviarlo desde jobs."
            />

            <InfoPanel
              title="Cliente"
              icon={<User className="h-4 w-4" />}
              tone="emerald"
              value={order.customerName || 'Sin nombre'}
              description={order.customerPhone || 'Sin teléfono'}
            />

            <InfoPanel
              title="Totales"
              icon={<Clock3 className="h-4 w-4" />}
              tone="indigo"
              value={money(order.totalCents, order.currency)}
              description={`${order.itemCount} unidad${order.itemCount === 1 ? '' : 'es'} · Subtotal ${money(order.subtotalCents, order.currency)}`}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default function OrderReceiptPage() {
  const { id } = useParams<{ id?: string }>()
  const [receiptImageUrl, setReceiptImageUrl] = useState<string | null>(null)

  const orderQuery = useQuery({
    queryKey: ['order-receipt-order', id],
    queryFn: () => ordersApi.getOrder(id!),
    enabled: Boolean(id),
    staleTime: 60_000,
  })

  const receiptInfoQuery = useQuery({
    queryKey: ['order-receipt-info', id],
    queryFn: () => ordersApi.getOrderReceiptInfo(id!),
    enabled: Boolean(id),
    staleTime: 30_000,
  })

  const receiptImageQuery = useQuery({
    queryKey: ['order-receipt-image', id, receiptInfoQuery.data?.downloadUrl ?? null],
    queryFn: () => ordersApi.getOrderReceiptImage(id!),
    enabled: Boolean(id && receiptInfoQuery.data?.downloadUrl),
    staleTime: 30_000,
  })

  useEffect(() => {
    const blob = receiptImageQuery.data
    if (!blob) {
      setReceiptImageUrl(null)
      return
    }

    const objectUrl = URL.createObjectURL(blob)
    setReceiptImageUrl(objectUrl)
    return () => URL.revokeObjectURL(objectUrl)
  }, [receiptImageQuery.data])

  const downloadFileName = useMemo(
    () => receiptInfoQuery.data?.fileName || `${orderQuery.data?.orderNumber || 'receipt'}.png`,
    [orderQuery.data?.orderNumber, receiptInfoQuery.data?.fileName]
  )

  const copyRoute = async () => {
    const route = receiptInfoQuery.data?.downloadUrl
    if (!route) return

    try {
      await navigator.clipboard.writeText(route)
      toast.success('Ruta copiada')
    } catch {
      toast.error('No se pudo copiar la ruta')
    }
  }

  const downloadReceiptPng = () => {
    if (!receiptImageUrl || typeof window === 'undefined') return

    const link = document.createElement('a')
    link.href = receiptImageUrl
    link.download = downloadFileName
    link.rel = 'noopener'
    document.body.appendChild(link)
    link.click()
    link.remove()
  }

  const downloadReceiptPdf = async () => {
    if (!id) return

    try {
      const blob = await ordersApi.getOrderReceiptPdf(id)
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = downloadFileName.replace(/\.png$/i, '.pdf')
      link.rel = 'noopener'
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.setTimeout(() => URL.revokeObjectURL(url), 1000)
    } catch {
      toast.error('No se pudo descargar el PDF')
    }
  }

  const printReceipt = () => {
    if (typeof window === 'undefined') return
    window.print()
  }

  if (!id) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4 py-12">
        <div className="max-w-sm rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-sm">
          <p className="text-lg font-semibold text-slate-900">Falta el ID de la orden</p>
          <p className="mt-2 text-sm text-slate-500">No pudimos preparar el recibo porque la ruta no recibió una orden válida.</p>
        </div>
      </div>
    )
  }

  if (orderQuery.isLoading || receiptInfoQuery.isLoading || receiptImageQuery.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4 py-12">
        <div className="flex items-center gap-3 rounded-full border border-slate-200 bg-white px-4 py-3 text-slate-600 shadow-sm">
          <Loader2 className="h-5 w-5 animate-spin text-[#075f55]" />
          Generando recibo en backend...
        </div>
      </div>
    )
  }

  if (orderQuery.isError || receiptInfoQuery.isError || receiptImageQuery.isError || !orderQuery.data) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4 py-12">
        <div className="max-w-sm rounded-2xl border border-rose-200 bg-white p-6 text-center shadow-sm">
          <p className="text-lg font-semibold text-rose-700">No pudimos cargar el recibo</p>
          <p className="mt-2 text-sm text-slate-500">Puede que la orden no exista, el archivo no se haya generado o no tengas acceso a ella.</p>
          <Link
            to="/app/orders"
            className="mt-4 inline-flex items-center justify-center rounded-full bg-[#075f55] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#064d46]"
          >
            Volver al listado
          </Link>
        </div>
      </div>
    )
  }

  return (
    <OrderReceiptView
      order={orderQuery.data}
      receiptInfo={receiptInfoQuery.data ?? null}
      receiptImageUrl={receiptImageUrl}
      onCopyRoute={copyRoute}
      onDownloadPng={downloadReceiptPng}
      onDownloadPdf={downloadReceiptPdf}
      onPrint={printReceipt}
    />
  )
}

function InfoCard({
  label,
  value,
  tone,
}: {
  label: string
  value: string
  tone?: 'emerald' | 'blue' | 'violet' | 'slate' | 'amber' | 'indigo' | 'rose'
}) {
  return (
    <div className={['rounded-2xl border p-4', tone ? infoToneClass(tone) : 'border-slate-200 bg-slate-50 text-slate-600'].join(' ')}>
      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] opacity-70">{label}</p>
      <p className="mt-2 text-sm font-semibold text-slate-900">{value}</p>
    </div>
  )
}

function InfoPanel({
  title,
  icon,
  tone,
  value,
  description,
  copyValue,
  onCopy,
}: {
  title: string
  icon: ReactNode
  tone: 'emerald' | 'blue' | 'violet' | 'slate' | 'amber' | 'indigo' | 'rose'
  value: string
  description?: string
  copyValue?: string
  onCopy?: () => Promise<void> | void
}) {
  return (
    <div className={['rounded-3xl border p-4 shadow-sm', infoToneClass(tone)].join(' ')}>
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] opacity-80">
        {icon}
        {title}
      </div>
      <p className="mt-3 break-all text-sm font-semibold text-slate-900">{value}</p>
      {description ? <p className="mt-2 text-sm text-slate-600">{description}</p> : null}
      {copyValue && onCopy ? (
        <button
          type="button"
          onClick={onCopy}
          className="mt-3 inline-flex items-center gap-2 rounded-full border border-white/70 bg-white/80 px-3 py-1.5 text-xs font-semibold text-slate-700 transition-colors hover:bg-white"
        >
          <Link2 className="h-3.5 w-3.5" />
          Copiar ruta
        </button>
      ) : null}
    </div>
  )
}
